import React, { useContext, useEffect, useLayoutEffect, useState } from 'react'
import { ChatbotContext } from '../context/ChatbotProvider'
import { ChatFile } from '../ChatFile'
import { copyIconString, requestAbortedMsg, getFileExtension } from '../utils/constants'
import { hasImages, formatMarkdown } from '../utils/TextUtils'
import { ChatImage } from '../ChatImage'
import { checkIsDrawerMode, prepareCitations } from '../utils/helpers'
import { useTableActions } from './useTableActions'
import { useAuthenticatedDownload } from './useAuthenticatedDownload'
import { useSelector } from 'react-redux'
import { appInsights } from '../../../../_services/AppInsights'
import { EventType } from '../../../../utility/types'
import { useThemeManager } from '../../../../_hooks/useThemeManager'
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react'
// @ts-ignore
import { Badge } from 'dsm-react-lib'

// Collapse long user prompts beyond ~6 lines. Padding lives on the wrapper OUTSIDE the
// clipped/measured element, so it's never eaten by the overflow-hidden + max-height clip.
const USER_PROMPT_VISIBLE_LINES = 6
const USER_PROMPT_LINE_HEIGHT = 24 // --font-body-default line-height
const USER_PROMPT_COLLAPSED_TEXT_HEIGHT = USER_PROMPT_VISIBLE_LINES * USER_PROMPT_LINE_HEIGHT
let inlineCitationRequestSequence = 0

interface ChatHistoryTextContentProps {
  message: any
  responseIndex?: number
  isPromptEditing: boolean
  nodeRef?: React.RefObject<HTMLDivElement>
}

const ChatMessageContent: React.FC<ChatHistoryTextContentProps> = ({
  message,
  responseIndex,
  isPromptEditing,
  nodeRef
}) => {
  const { response = [], activeResponseIndex, type, role, resources, parentId } = message || {}
  const isUser = role === 'user'
  const currentResponseId = responseIndex === undefined ? activeResponseIndex : responseIndex
  const msgContent = response[currentResponseId]?.content || ''
  const { isStreaming, setSelectedMessage, currentChat } = useContext(ChatbotContext)
  const [messageContent, setMessageContent] = useState<any>(null)
  const isDrawerMode = checkIsDrawerMode()
  const userId = useSelector((state: any) => state?.profileReducer?.profile?.email)
  const { downloadWithAuth } = useAuthenticatedDownload()
  const { isDarkTheme: isDark } = useThemeManager()
  const theme = isDark ? 'dark' : 'primary'
  const [isPromptExpanded, setIsPromptExpanded] = useState(false)
  const [isPromptOverflowing, setIsPromptOverflowing] = useState(false)
  const [promptFullHeight, setPromptFullHeight] = useState(0)

  useTableActions({ messageContent, userId, conversationId: currentChat?.id })

  const handleInlineCitationClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement
    const citationButton = target.closest<HTMLButtonElement>('[data-inline-citation-index]')
    if (!citationButton) return

    const citationIndex = Number(citationButton.dataset.inlineCitationIndex)
    const pageNumber = Number(citationButton.dataset.pageNumber)
    const citationFileName = citationButton.dataset.citationFileName
    if (!Number.isInteger(citationIndex) || citationIndex < 0) return

    appInsights.trackEvent({
      name: EventType.CitationPreviewInteraction,
      properties: {
        userId,
        conversationId: currentChat?.id,
        actionType: 'inline_citation_click',
        citationIndex,
        pageNumber: Number.isFinite(pageNumber) ? pageNumber : undefined,
        timestamp: new Date().toISOString()
      }
    })

    setSelectedMessage({
      parentId,
      type: 'citations',
      inlineCitationIndex: citationIndex,
      inlineCitationPageNumber: Number.isFinite(pageNumber) ? pageNumber : undefined,
      inlineCitationFileName: citationFileName,
      inlineCitationResponseIndex: currentResponseId,
      inlineCitationTrigger: citationButton,
      inlineCitationRequestId: ++inlineCitationRequestSequence
    })
  }

  // scrollHeight always reflects the full (unclipped) content height, so it doubles as both
  // the overflow check and the animation target for the expanded max-height.
  useLayoutEffect(() => {
    if (!isUser) return undefined
    const el = nodeRef?.current
    if (!el) return undefined

    const measure = () => {
      setPromptFullHeight(el.scrollHeight)
      setIsPromptOverflowing(el.scrollHeight > USER_PROMPT_COLLAPSED_TEXT_HEIGHT + 1)
    }
    measure()

    let observer: ResizeObserver | undefined
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(measure)
      observer.observe(el)
    }

    return () => observer?.disconnect()
  }, [isUser, messageContent, nodeRef])

  //This useEffect formats the raw message content into HTML using markupRawContent
  useEffect(() => {
    const formatContent = async () => {
      if (type === 'loader' || isUser) {
        setMessageContent(msgContent)
      } else {
        const messageResponse = message?.response[currentResponseId]
        const citations = messageResponse?.citations || []

        const updatedCitations = await prepareCitations(citations)
        const formattedMsg = formatMarkdown(
          msgContent,
          isStreaming && response[currentResponseId]?.id === null,
          updatedCitations,
          isDrawerMode
        )

        // Show the "forcefully stopped" message when either:
        // 1. The response ID starts with 'temp-' — the stream was aborted before
        //    receiving RUN_FINISHED in the current session (normal completion sets
        //    a proper UUID in RUN_FINISHED).
        // 2. The persisted `aborted` flag is true — the message was aborted in a
        //    prior session and reloaded from history, where the backend has since
        //    assigned it a real UUID, so the temp- check alone would miss it.
        const responseId = response[currentResponseId]?.id
        const isPersistedAborted = response[currentResponseId]?.aborted === true

        const wasAborted =
          !isStreaming &&
          (isPersistedAborted || responseId?.toString().startsWith('temp-')) &&
          !formattedMsg.includes(requestAbortedMsg)

        setMessageContent(wasAborted ? `${formattedMsg} ${requestAbortedMsg}` : formattedMsg)
      }
    }
    formatContent()
  }, [msgContent, response[currentResponseId]?.id])

  // Add copy functionality
  useEffect(() => {
    const copyButtons: any = document.getElementsByClassName('copy-button') || []
    const arrayBtn = Array.from(copyButtons)
    arrayBtn?.forEach((button: any) => {
      button.addEventListener('click', () => {
        const codeBlock: any = button.closest('.code-block').querySelector('code')
        const codeText = codeBlock.innerText

        navigator.clipboard
          .writeText(codeText)
          .then(() => {
            appInsights.trackEvent({
              name: EventType.ChatCodeCopied,
              properties: {
                userId,
                conversationId: currentChat?.id,
                artifactType: 'code',
                format: 'clipboard',
                timestamp: new Date().toISOString()
              }
            })
            button.innerHTML = 'Copied!'
            setTimeout(() => {
              button.innerHTML = `${copyIconString}`
            }, 2000)
          })
          .catch((err) => {
            console.error('Failed to copy text: ', err)
          })
      })
    })

    const downloadButtons: any = document.getElementsByClassName('download-button') || []
    const downloadArray = Array.from(downloadButtons)
    downloadArray?.forEach((button: any) => {
      button.onclick = () => {
        const codeBlock: any = button.closest('.code-block')?.querySelector('code')
        const codeText = codeBlock?.innerText || ''
        const lang = button.getAttribute('data-lang') || 'text'
        const extension = getFileExtension(lang)
        const blob = new Blob([codeText], { type: 'text/plain;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const anchor = document.createElement('a')
        anchor.href = url
        anchor.download = `code-snippet.${extension}`
        document.body.appendChild(anchor)
        anchor.click()
        document.body.removeChild(anchor)
        URL.revokeObjectURL(url)
        appInsights.trackEvent({
          name: EventType.ChatCodeDownloaded,
          properties: {
            userId,
            conversationId: currentChat?.id,
            artifactType: 'code',
            format: extension,
            timestamp: new Date().toISOString()
          }
        })
      }
    })

    const maximizeButtons = document.getElementsByClassName('maximize-button') || []
    const maximizeArray = Array.from(maximizeButtons)

    const handleMaximizeClick = (button: any) => {
      const codeBlock = button.closest('.code-block')?.querySelector('code')
      const codeText = codeBlock?.innerText

      // Set code and determine the language
      if (codeBlock) {
        const languageClass = Array.from(codeBlock.classList).find((cls: any) =>
          cls.startsWith('language-')
        )
        const detectedLanguage = languageClass
          ? (languageClass as string).replace('language-', '')
          : 'Unknown'
        setSelectedMessage({ code: codeText || '', lang: detectedLanguage, type: 'maximize' })
      }
    }

    maximizeArray.forEach((button: any) => {
      if (!isDrawerMode) {
        button.classList.remove('d-none') // Show the button
        button.onclick = () => handleMaximizeClick(button)
      } else {
        button.classList.add('d-none') // Hide the button
      }
    })

    // Artifact download buttons — marked renders artifact cards as raw HTML (outside React's
    // tree), so we attach click handlers via DOM query, same pattern as copy/download above.
    const artifactDownloadButtons: any =
      document.getElementsByClassName('artifact-download-btn') || []
    const artifactArray = Array.from(artifactDownloadButtons)
    artifactArray?.forEach((button: any) => {
      button.onclick = () => {
        const url = button.getAttribute('data-url')
        const filename = button.getAttribute('data-filename') || 'download'
        if (url) {
          downloadWithAuth(url, filename)
          appInsights.trackEvent({
            name: EventType.ChatArtifactDownloaded,
            properties: {
              userId,
              conversationId: currentChat?.id,
              artifactType: 'artifact',
              format: filename?.split('.').pop() || 'unknown',
              timestamp: new Date().toISOString()
            }
          })
        }
      }
    })

    return () => {
      maximizeArray.forEach((button: any) => {
        button.onclick = null
      })
      downloadArray.forEach((button: any) => {
        button.onclick = null
      })
      artifactArray.forEach((button: any) => {
        button.onclick = null
      })
    }
  }, [messageContent])

  return isUser ? (
    <>
      {resources?.length > 0 && (
        <div className="flex flex-wrap gap-2.5 justify-end mb-1">
          {resources.map((file: any, index: number) =>
            file.type === 'image' ? (
              <ChatImage key={index} file={file} hasRemove={false} size={'15rem'} />
            ) : (
              <ChatFile key={index} file={file} hasRemove={false} onRemoveClick={null} />
            )
          )}
        </div>
      )}
      {messageContent && (
        <div className="relative max-w-[70%]">
          <div
            className={`pt-1 px-1 pb-2 rounded-lg theme-user-prompt-surface theme-text-heading transition-colors duration-300 ease-in-out ${
              isPromptEditing ? 'hidden' : ''
            }`}>
            <div
              ref={nodeRef}
              className={`text-body-default whitespace-pre-wrap overflow-hidden transition-[max-height] duration-300 ease-in-out ${
                isPromptEditing ? 'hidden' : ''
              }`}
              style={
                isPromptOverflowing
                  ? {
                      maxHeight: isPromptExpanded
                        ? promptFullHeight
                        : USER_PROMPT_COLLAPSED_TEXT_HEIGHT
                    }
                  : undefined
              }>
              {messageContent}
            </div>
          </div>
          {isPromptOverflowing && !isPromptEditing && (
            <Badge
              type="pill"
              size="small"
              label={
                isPromptExpanded ? (
                  <IconChevronUp color="var(--icon-muted)" className="!w-5 !h-5" />
                ) : (
                  <IconChevronDown color="var(--icon-muted)" className="!w-5 !h-5" />
                )
              }
              onClick={() => setIsPromptExpanded((prev) => !prev)}
              aria-label={isPromptExpanded ? 'Collapse message' : 'Expand message'}
              data-testid="user-prompt-toggle"
              className="absolute bottom-2 right-4 !p-1 cursor-pointer !bg-[rgb(var(--overlay-muted-surface-rgb)/0.6)] hover:opacity-80 transition-opacity duration-200"
            />
          )}
        </div>
      )}
    </>
  ) : (
    <div
      ref={nodeRef}
      onClick={handleInlineCitationClick}
      className={`chat-history-content theme-text-subheading overflow-visible text-body-default ${theme}`}
      dangerouslySetInnerHTML={{
        //@ts-ignore
        __html: messageContent
      }}
    />
  )
}

export default ChatMessageContent
